/**
 * Organization API Constants
 * All organization-level API endpoint definitions
 */

export const ORGANIZATION_API_ENDPOINTS = {
  BASE: (orgSlug: string) => `/orgs/${orgSlug}`,
  /** Core + purchased add-on features for role management (add-on entitlement aware). */
  FEATURES: (orgSlug: string) => `/orgs/${orgSlug}/features`,
  DASHBOARD: (orgSlug: string) => `/orgs/${orgSlug}/dashboard`,
  ANALYTICS: {
    OVERVIEW: (orgSlug: string) => `/orgs/${orgSlug}/analytics/overview`,
    FINANCE: (orgSlug: string) => `/orgs/${orgSlug}/analytics/finance`,
  },
  BRANCHES: {
    LIST: (orgSlug: string) => `/orgs/${orgSlug}/branches`,
    CREATE: (orgSlug: string) => `/orgs/${orgSlug}/branches`,
    GET: (orgSlug: string, branchSlug: string) => `/orgs/${orgSlug}/branches/${branchSlug}`,
    UPDATE: (orgSlug: string, branchSlug: string) => `/orgs/${orgSlug}/branches/${branchSlug}`,
    DELETE: (orgSlug: string, branchSlug: string) => `/orgs/${orgSlug}/branches/${branchSlug}`,
    SUBSCRIPTION: (orgSlug: string, branchSlug: string) => `/orgs/${orgSlug}/branches/${branchSlug}/subscription`,
    PAYMENT: (orgSlug: string, branchSlug: string) => `/orgs/${orgSlug}/branches/${branchSlug}/payment`,
  },
  USERS: {
    LIST: (orgSlug: string) => `/orgs/${orgSlug}/users`,
    CREATE: (orgSlug: string) => `/orgs/${orgSlug}/users`,
    GET: (orgSlug: string, userId: string) => `/orgs/${orgSlug}/users/${userId}`,
    UPDATE: (orgSlug: string, userId: string) => `/orgs/${orgSlug}/users/${userId}`,
    DELETE: (orgSlug: string, userId: string) => `/orgs/${orgSlug}/users/${userId}`,
  },
  APPROVAL_POLICIES: {
    LIST: (orgSlug: string) => `/orgs/${orgSlug}/admin/approval-policies`,
    CREATE: (orgSlug: string) => `/orgs/${orgSlug}/admin/approval-policies`,
    UPDATE: (orgSlug: string, id: string) => `/orgs/${orgSlug}/admin/approval-policies/${id}`,
    DELETE: (orgSlug: string, id: string) => `/orgs/${orgSlug}/admin/approval-policies/${id}`,
  },
  ROLES: {
    LIST: (orgSlug: string) => `/orgs/${orgSlug}/permissions/all-organization-roles`,
    CREATE: (orgSlug: string) => `/orgs/${orgSlug}/permissions/create-organization-role`,
    GET: (orgSlug: string, roleId: string) => `/orgs/${orgSlug}/permissions/organization-role/${roleId}`,
    UPDATE: (orgSlug: string, roleId: string) => `/orgs/${orgSlug}/permissions/update-organization-role/${roleId}`,
    DELETE: (orgSlug: string, roleId: string) => `/orgs/${orgSlug}/permissions/delete-organization-role/${roleId}`,
    AVAILABLE_PERMISSIONS: (orgSlug: string) => `/orgs/${orgSlug}/permissions/available-organization-permissions`,
    SUBSCRIBED_FEATURES: (orgSlug: string) => `/orgs/${orgSlug}/permissions/get-all-organization-subscribed-features`,
  },
  GROUPS: {
    LIST: (orgSlug: string) => `/orgs/${orgSlug}/permissions/all-organization-groups`,
    CREATE: (orgSlug: string) => `/orgs/${orgSlug}/permissions/create-organization-group`,
    GET: (orgSlug: string, groupId: string) => `/orgs/${orgSlug}/permissions/organization-group/${groupId}`,
    UPDATE: (orgSlug: string, groupId: string) => `/orgs/${orgSlug}/permissions/update-organization-group/${groupId}`,
    DELETE: (orgSlug: string, groupId: string) => `/orgs/${orgSlug}/permissions/delete-organization-group/${groupId}`,
  },
  COMMUNICATION: {
    ANNOUNCEMENTS: (orgSlug: string) => `/orgs/${orgSlug}/announcements`,
    ACADEMIC_CALENDAR: (orgSlug: string) => `/orgs/${orgSlug}/academic-calendar`,
    CLASS_MANAGEMENT: (orgSlug: string) => `/orgs/${orgSlug}/class-management`,
  },
  /** Session by slug (access token only; use when context token may be unset, e.g. payment step reload). */
  ONBOARDING_SESSION_BY_SLUG: '/onboarding/session',
  ONBOARDING_SESSION_COMPLETE: '/onboarding/session/complete',
  SUBSCRIPTION_V2: {
    ONBOARDING_SESSION: (orgSlug: string) => `/orgs/${orgSlug}/onboarding/session`,
    PLAN_OPTIONS: (orgSlug: string) => `/orgs/${orgSlug}/onboarding/plan-options`,
    ACTIVATE_PILOT: (orgSlug: string) => `/orgs/${orgSlug}/subscriptions/activate-pilot`,
    CREATE_PAID: (orgSlug: string) => `/orgs/${orgSlug}/subscriptions/create-paid`,
    CREATE_INVOICE_ORDER: (orgSlug: string) => `/orgs/${orgSlug}/subscriptions/invoices/create-order`,
    CANCEL_PENDING_PAID: (orgSlug: string) => `/orgs/${orgSlug}/subscriptions/cancel-pending-paid`,
    RENEW: (orgSlug: string) => `/orgs/${orgSlug}/subscriptions/renew`,
    USAGE: (orgSlug: string) => `/orgs/${orgSlug}/subscriptions/usage`,
  },
  BILLING: {
    INVOICES: {
      LIST: (orgSlug: string) => `/orgs/${orgSlug}/invoices`,
      GET_BY_ID: (orgSlug: string, invoiceId: string) => `/orgs/${orgSlug}/invoices/${invoiceId}`,
      PAY: (orgSlug: string, invoiceId: string) => `/orgs/${orgSlug}/invoices/${invoiceId}/pay`,
      DOWNLOAD: (orgSlug: string, invoiceId: string) => `/orgs/${orgSlug}/invoices/${invoiceId}/download`,
    },
    PAYMENT_METHODS: {
      LIST: (orgSlug: string, branchSlug: string) => `/orgs/${orgSlug}/org-branches/branches/${branchSlug}/payment-methods/get-customer-payment-methods`,
      GET_BY_ID: (orgSlug: string, branchSlug: string, paymentMethodId: string) => `/orgs/${orgSlug}/org-branches/branches/${branchSlug}/payment-methods/${paymentMethodId}/get-payment-method-details`,
      CREATE: (orgSlug: string, branchSlug: string) => `/orgs/${orgSlug}/org-branches/branches/${branchSlug}/payment-methods`,
      UPDATE: (orgSlug: string, branchSlug: string, paymentMethodId: string) => `/orgs/${orgSlug}/org-branches/branches/${branchSlug}/payment-methods/${paymentMethodId}`,
      RETRY_FAILED: (orgSlug: string, branchSlug: string, paymentMethodId: string) => `/orgs/${orgSlug}/org-branches/branches/${branchSlug}/payment-methods/${paymentMethodId}/retry-failed-payments`,
      VALIDATE: (orgSlug: string, branchSlug: string, paymentMethodId: string) => `/orgs/${orgSlug}/org-branches/branches/${branchSlug}/payment-methods/${paymentMethodId}/validate`,
      VALIDATION_STATUS: (orgSlug: string, branchSlug: string, paymentMethodId: string) => `/orgs/${orgSlug}/org-branches/branches/${branchSlug}/payment-methods/${paymentMethodId}/validation-status`,
    },
    PAYMENT_FAILURES: {
      LIST: (orgSlug: string) => `/orgs/${orgSlug}/payment-failures`,
      STATS: (orgSlug: string) => `/orgs/${orgSlug}/payment-failures/stats`,
    },
  },
  SETTINGS: {
    GET: (orgSlug: string) => `/orgs/${orgSlug}/settings`,
    UPDATE: (orgSlug: string) => `/orgs/${orgSlug}/settings`,
    FORMS: (orgSlug: string) => `/orgs/${orgSlug}/settings/forms`,
    /** Current user's settings (org context); userId from token */
    USER_SETTINGS: {
      GET: (orgSlug: string) => `/orgs/${orgSlug}/users/settings`,
      UPDATE: (orgSlug: string) => `/orgs/${orgSlug}/users/settings`,
    },
  },
  FORM_MANAGEMENT: {
    LIST: () => '/organization/form-management/configs',
    GET_BY_ID: (formConfigId: string) => `/organization/form-management/configs/${formConfigId}`,
    CREATE: () => '/organization/form-management/configs',
    UPDATE: (formConfigId: string) => `/organization/form-management/configs/${formConfigId}`,
    DELETE: (formConfigId: string) => `/organization/form-management/configs/${formConfigId}`,
    COPY: (formConfigId: string) => `/organization/form-management/configs/${formConfigId}/copy`,
    PLATFORM_TEMPLATES: () => '/organization/form-management/platform-templates',
    SYNC_TO_BRANCHES: (formConfigId: string) =>
      `/organization/form-management/configs/${formConfigId}/sync-to-branches`,
  },
  THEMES: {
    LIST: (orgSlug: string) => `/orgs/${orgSlug}/themes`,
    STATS: (orgSlug: string) => `/orgs/${orgSlug}/themes/stats`,
    GET_BY_ID: (orgSlug: string, id: string) => `/orgs/${orgSlug}/themes/${id}`,
    GET_CURRENT: (orgSlug: string) => `/orgs/${orgSlug}/themes/current`,
    CREATE: (orgSlug: string) => `/orgs/${orgSlug}/themes`,
    UPDATE: (orgSlug: string, id: string) => `/orgs/${orgSlug}/themes/${id}`,
    DELETE: (orgSlug: string, id: string) => `/orgs/${orgSlug}/themes/${id}`,
    SET_DEFAULT: (orgSlug: string, id: string) => `/orgs/${orgSlug}/themes/${id}/set-default`,
  },
  FONT_SIZES: {
    LIST: (orgSlug: string) => `/orgs/${orgSlug}/font-sizes`,
    STATS: (orgSlug: string) => `/orgs/${orgSlug}/font-sizes/stats`,
    GET_BY_ID: (orgSlug: string, fontSizeId: string) => `/orgs/${orgSlug}/font-sizes/${fontSizeId}`,
    GET_CURRENT: (orgSlug: string) => `/orgs/${orgSlug}/font-sizes/current`,
    CREATE: (orgSlug: string) => `/orgs/${orgSlug}/font-sizes`,
    UPDATE: (orgSlug: string, fontSizeId: string) => `/orgs/${orgSlug}/font-sizes/${fontSizeId}`,
    DELETE: (orgSlug: string, fontSizeId: string) => `/orgs/${orgSlug}/font-sizes/${fontSizeId}`,
    SET_DEFAULT: (orgSlug: string, fontSizeId: string) => `/orgs/${orgSlug}/font-sizes/${fontSizeId}/set-default`,
  },
}
